import React from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { Text } from 'wix-ui-tpa/cssVars';
import { classes, st } from './Details.st.css';
import { AlignmentOptions, ISection, SectionTypes } from '../../types';
import Section from '../Section';
import { DetailsSectionViewModel } from '../../../../service-page-view-model/details-section-view-model/detailsSectionViewModel';
import { useGlobalAlignment } from '../useGlobalAlignment';
import settingsParams from '../../settingsParams';
import { useSettings } from '@wix/tpa-settings/react';
import { useAddVisibilityClass } from '../../hooks/useAddVisibilityClass';
import { useVisibilityCheck } from '../../hooks/useVisibilityCheck';
import { useSectionVisibilityCheck } from '../../hooks/useSectionVisibilityCheck';

type DetailsProps = {
  viewModel?: DetailsSectionViewModel;
  section: ISection;
  className?: string;
};

const DetailsComponent: React.FC<DetailsProps> = ({ viewModel, className }) => {
  const { isMobile, isRTL } = useEnvironment();
  const settings = useSettings();
  const globalAlignment = useGlobalAlignment();
  const { shouldSectionBookButtonBeVisible } = useSectionVisibilityCheck();
  const { addVisibilityClass } = useAddVisibilityClass();
  const { shouldBeVisible } = useVisibilityCheck();

  const buttonAlignment =
    settings.get(settingsParams.detailsBoxAlignment) ===
    AlignmentOptions.STRETCH
      ? settings.get(settingsParams.detailsButtonAlignment)
      : settings.get(settingsParams.detailsBoxAlignment);
  const textAlignment =
    (settings.get(settingsParams.detailsBoxAlignment) !==
    AlignmentOptions.STRETCH
      ? settings.get(settingsParams.detailsBoxAlignment)
      : settings.get(settingsParams.detailsTextAlignment)) || globalAlignment;
  const boxWidth =
    settings.get(settingsParams.detailsBoxAlignment) ===
    AlignmentOptions.STRETCH
      ? '100%'
      : undefined;
  const withBookButton =
    shouldSectionBookButtonBeVisible(SectionTypes.DETAILS) &&
    viewModel?.isBookable &&
    viewModel?.isFullyBooked !== true;
  const showDuration =
    shouldBeVisible('detailsDurationVisibility') && viewModel?.duration;
  const showPrice =
    shouldBeVisible('detailsPriceVisibility') && viewModel?.price;
  const showLocation =
    shouldBeVisible('detailsLocationVisibility') && viewModel?.location;
  const showLocations =
    shouldBeVisible('detailsLocationVisibility') &&
    viewModel?.locations?.length;
  const showAvailability =
    settings.get(settingsParams.courseAvailability) &&
    !!viewModel?.numberOfSpotsLeft;

  const content: JSX.Element[] = [];
  if (showDuration) {
    content.push(
      <Text
        key="details-duration"
        className={addVisibilityClass(
          classes.box,
          classes.durationVisibility,
          'detailsDurationVisibility',
        )}
        data-hook="details-duration"
        tagName="li"
      >
        <span aria-hidden>{viewModel?.duration}</span>
        <span
          className={classes.srOnly}
          data-hook="details-duration-aria-label"
        >
          {viewModel?.durationAria}
        </span>
      </Text>,
    );
  }
  if (showPrice) {
    content.push(
      <li
        key="details-price"
        className={addVisibilityClass(
          '',
          classes.priceVisibility,
          'detailsPriceVisibility',
        )}
      >
        <div className={classes.srOnly} data-hook="sr-only-details-price">
          {viewModel?.ariaPrice}
        </div>
        <div aria-hidden={true} data-hook="aria-hidden-details-price">
          <Text className={classes.box} data-hook="details-price" tagName="div">
            {viewModel?.price}
          </Text>
        </div>
      </li>,
    );
  }
  if (showLocation) {
    content.push(
      <Text
        key="details-location"
        className={addVisibilityClass(
          classes.box,
          classes.locationVisibility,
          'detailsLocationVisibility',
        )}
        data-hook="details-location"
        tagName="li"
      >
        {viewModel?.location}
      </Text>,
    );
  }

  if (showLocations) {
    content.push(
      <Text
        key="details-locations"
        className={classes.box}
        data-hook="details-locations"
        tagName="li"
      >
        {viewModel?.locations?.map((location, index) => {
          return (
            <>
              {location}
              {index !== Number(viewModel?.locations?.length) - 1 && (
                <span className={classes.locationDivider}>|</span>
              )}
            </>
          );
        })}
      </Text>,
    );
  }

  const availability = showAvailability ? (
    <Text
      key="details-course-availability"
      className={classes.courseAvailability}
      data-hook="details-course-availability"
      tagName="p"
    >
      {viewModel?.numberOfSpotsLeft}
    </Text>
  ) : null;
  return withBookButton || !!content.length ? (
    <Section
      sectionType={SectionTypes.DETAILS}
      withBookButton={withBookButton}
      buttonAlignment={buttonAlignment}
      childrenAlignment={settings.get(settingsParams.detailsBoxAlignment)}
      availability={availability}
    >
      <ul
        style={{ width: boxWidth }}
        className={st(
          classes.root,
          {
            textAlignment,
            isMobile,
            isRTL,
          },
          className,
        )}
        data-hook="details-wrapper"
      >
        {content}
      </ul>
    </Section>
  ) : null;
};

export default DetailsComponent;
